import type { Moment } from 'moment';

import type { ChatTaskStatus } from '@frontend/api';

import type { ChatId } from 'modules/common/chat/data/types';

import type { ActionsOf } from 'store/types';

import { notify } from 'utils/reduxUtils';

export const types = {
  EDIT_TASK_ERROR: 'chat/tasks/EDIT_TASK_ERROR',
  EDIT_TASK_REQUEST: 'chat/tasks/EDIT_TASK_REQUEST',
  EDIT_TASK_SUCCESS: 'chat/tasks/EDIT_TASK_SUCCESS',
  TASK_ASSIGNMENT_RESET: 'chat/tasks/TASK_ASSIGNMENT_RESET',
  TASK_ASSIGNMENT_SET: 'chat/tasks/TASK_ASSIGNMENT_SET',
} as const;

export type TaskIdPayload = {
  taskId: number;
};

/** Reassigning sends `assignedToId` and `dueDate`; resolving sends only `status` */
export type EditTaskRequestPayload = TaskIdPayload & {
  assignedToId?: ChatId;
  dueDate?: Moment | null;
  status?: ChatTaskStatus;
};

export type TaskAssignmentSetPayload = {
  assignedUserFullName: string | null;
  assignedUserId: ChatId | null;
  dueDate: Moment | null;
};

export const editTask = {
  // NB: `notify` only forwards the first argument, so `taskId` is always
  // undefined when this is called through the wrapper.
  error: notify(
    (error: unknown, taskId?: number | null) => ({
      type: types.EDIT_TASK_ERROR,
      payload: { error, taskId },
    }),
    'Failed to save note',
  ),
  request: ({
    assignedToId,
    dueDate,
    status,
    taskId,
  }: EditTaskRequestPayload) => ({
    type: types.EDIT_TASK_REQUEST,
    payload: { assignedToId, dueDate, status, taskId },
  }),
  success: ({ taskId }: TaskIdPayload) => ({
    type: types.EDIT_TASK_SUCCESS,
    payload: { taskId },
  }),
};

export const taskAssignment = {
  reset: () => ({
    type: types.TASK_ASSIGNMENT_RESET,
  }),
  set: ({
    assignedUserFullName,
    assignedUserId,
    dueDate,
  }: TaskAssignmentSetPayload) => ({
    type: types.TASK_ASSIGNMENT_SET,
    payload: { assignedUserFullName, assignedUserId, dueDate },
  }),
};

export type ChatTasksAction = ActionsOf<{
  editTask: typeof editTask;
  taskAssignment: typeof taskAssignment;
}>;
